import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Observable, throwError } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AnalyticsDataDirective } from 'src/app/directives/analytics-data.directive';
import { AnalyticsDirective } from 'src/app/directives/analytics.directive';
import { CommunityItem } from 'src/app/models/community-item';
import { CommunityItemService } from 'src/app/services/community-item.service';
import { UserAuthService } from 'src/app/state/user-auth/user-auth.service';

@Component({
    imports: [AnalyticsDataDirective, AnalyticsDirective, AngularSvgIconModule],
    selector: 'rdx-like-button',
    styleUrls: ['./like-button.component.scss'],
    templateUrl: './like-button.component.html',
})
export class LikeButtonComponent implements OnInit, OnDestroy {
    @Input() communityItem: CommunityItem = new CommunityItem();
    @Input() iconColor: 'primary' | 'black' = 'primary';
    @Input() iconOnly = false;

    isAuthenticated$: Observable<boolean>;
    isLiked = true;
    isDestroyed = false;
    likeCount = 0;

    private saveSuccessString = $localize`Liked`;
    private saveErrorString = $localize`Unable to Like`;
    private selectAnItem = $localize`Please select an item`;
    private deleteSuccessString = $localize`Successfully Removed`;
    private deleteErrorString = $localize`Unable to Remove`;

    constructor(
        private communityItemService: CommunityItemService,
        private matSnackBar: MatSnackBar,
        private userAuthService: UserAuthService
    ) {}

    ngOnInit(): void {
        this.isAuthenticated$ = this.userAuthService.isAuthenticated$;
        this.likeCount = this.communityItem.communityFeedback.numberOfLikes;
        this.isLiked =
            this.communityItem.communityFeedback.myLikeType === 'Like';
    }

    ngOnDestroy() {
        this.isDestroyed = true;
    }

    like() {
        event?.stopPropagation();
        if (!this.communityItem.id) {
            this.matSnackBar.open(this.selectAnItem);
            return;
        }

        this.isAuthenticated$
            .pipe(
                take(1),
                switchMap((isAuthenticated) => {
                    if (!isAuthenticated) {
                        return throwError(() => 'not authenticated');
                    } else {
                        return this.communityItemService.like(
                            this.communityItem,
                            'Like'
                        );
                    }
                })
            )
            .subscribe({
                error: (error) => {
                    if (error === 'not authenticated') {
                        this.userAuthService.promptAuthenticationAndNavigate();
                        return;
                    }

                    this.isLiked = false;
                    this.matSnackBar.open(this.saveErrorString);
                },
                next: (item) => {
                    this.communityItem = item;
                    this.isLiked = true;
                    this.likeCount =
                        this.communityItem.communityFeedback.numberOfLikes;
                    this.matSnackBar.open(this.saveSuccessString);
                },
            });
    }

    unLike() {
        event?.stopPropagation();
        if (!this.communityItem.id) {
            this.matSnackBar.open(this.selectAnItem);
            return;
        }

        this.isAuthenticated$
            .pipe(
                take(1),
                switchMap((isAuthenticated) => {
                    if (!isAuthenticated) {
                        return throwError(() => 'not authenticated');
                    } else {
                        return this.communityItemService.like(
                            this.communityItem,
                            'Unknown'
                        );
                    }
                })
            )
            .subscribe({
                error: (error) => {
                    if (error === 'not authenticated') {
                        this.userAuthService.promptAuthenticationAndNavigate();
                        return;
                    }

                    this.isLiked = false;
                    this.matSnackBar.open(this.deleteErrorString);
                },
                next: (item) => {
                    this.communityItem = item;
                    this.isLiked = false;
                    this.likeCount =
                        this.communityItem.communityFeedback.numberOfLikes;
                    this.matSnackBar.open(this.deleteSuccessString);
                },
            });
    }
}
